import {
  type FilterOption,
  type FilterOptionSelect,
  type FilterOptionValue,
  FilterTypes,
} from 'features/filters/types'

interface CustomerGroupSpec {
  myLabel: string
  competitorLabel: string
  members: (option: FilterOptionSelect) => string[] | undefined
}

// The groupable breakdowns whose picker values split into the customer's own
// ("My Brands") and everyone else's ("Competitor Brands"). The group labels
// are not backend values — a selection stores the child values.
const BRAND_GROUPS: CustomerGroupSpec = {
  myLabel: 'My Brands',
  competitorLabel: 'Competitor Brands',
  members: (option) => option.customer_brands,
}

const CUSTOMER_GROUPS: Record<string, CustomerGroupSpec> = {
  brand: BRAND_GROUPS,
  product_brand: BRAND_GROUPS,
  product_hierarchy: {
    myLabel: 'My Products',
    competitorLabel: 'Competitor Products',
    members: (option) => option.customer_products,
  },
}

interface CustomerGroup {
  label: string
  children: FilterOptionValue[]
}

/**
 * The My / Competitor groups of a brand or product option, each with the
 * option values it holds. A group needs two or more children — the same
 * threshold as theme parents — so it never just duplicates a single child.
 * Empty for any other field, or when the option carries no membership list.
 */
function customerGroupsOf(option: FilterOption): CustomerGroup[] {
  const spec = CUSTOMER_GROUPS[option.field]
  if (!spec || option.filter_type !== FilterTypes.SELECT) return []
  const members = new Set(spec.members(option) ?? [])
  if (members.size === 0) return []
  const mine = option.values.filter((value) => members.has(value.value))
  const competitor = option.values.filter((value) => !members.has(value.value))
  return [
    { label: spec.myLabel, children: mine },
    { label: spec.competitorLabel, children: competitor },
  ].filter((group) => group.children.length >= 2)
}

/**
 * One synthetic value per customer group, selecting the union of its
 * children's values and summing their counts. Shaped like
 * `buildParentThemeValues`' output so the "All" list treats both alike.
 */
export function buildCustomerGroupValues(
  option: FilterOption,
): FilterOptionValue[] {
  return customerGroupsOf(option).map(({ label, children }) => ({
    value: label,
    n_posts: children.reduce((sum, child) => sum + (child.n_posts ?? 0), 0),
    identifiers: children.map((child) => child.value),
  }))
}

/**
 * Display names for a brand or product selection, with every fully-selected
 * group rolled up to its own label. Names that are not in the option (stale
 * or label-only) pass through, and first-appearance order is kept.
 */
export function rollUpCustomerGroupNames(
  selectedNames: string[],
  option: FilterOption,
): string[] {
  const labelOfName = new Map<string, string>()
  const selected = new Set(selectedNames)
  for (const { label, children } of customerGroupsOf(option)) {
    if (children.every((child) => selected.has(child.value))) {
      for (const child of children) labelOfName.set(child.value, label)
    }
  }
  return [
    ...new Set(selectedNames.map((name) => labelOfName.get(name) ?? name)),
  ]
}
